import { all, put, select, takeEvery } from 'redux-saga/effects';
import { ActionType } from 'typesafe-actions';
import { v4 as uuidv4 } from 'uuid';
import { uniq } from 'ramda';

import { KnmVersionsEnum } from 'APIPackage/KnmModule/enums/KnmVersionsEnum';
import { IKnmLink } from 'APIPackage/KnmModule/interfaces/IKnmLink';
import { fetchKnmLinks } from 'APIPackage/KnmModule/services/knmAdditionalDataApi';
import { knmGetSuccess } from 'KnmPackage/MainModule/actions/knmActions';
import { KnmActionsEnum } from 'KnmPackage/MainModule/enums/KnmActionsEnum';
import { MspCodesEnum } from 'APIPackage/KnmModule/enums/MspCodesEnum';
import {
  DATE_FORMAT_ISO_FNS,
  DATE_FORMAT_LONG_FNS,
  DATE_FORMAT_LONG_MINUTE_FNS,
  SYSTEM_DATE_FORMAT_FNS,
} from 'CorePackage/MainModule/constants/dateFormats';
import { convertInitialKnmDateToFormDate } from 'KnmPackage/MainModule/common/convertInitialKnmDateToFormDate';
import { inspectionDecisionOptions } from 'KnmPackage/MainModule/constants/inspectionDecisionOptions';
import { InspectionDecisionsEnum } from 'KnmPackage/MainModule/enums/InspectionDecisionsEnum';
import { createKnmTypeSelectOption } from 'CorePackage/CatalogsModule/common/createKnmTypeSelectOption';
import { createKnmExecutionTypeSelectOption } from 'CorePackage/CatalogsModule/common/createKnmExecutionTypeSelectOption';
import { createTestPurchaseMethodSelectOption } from 'CorePackage/CatalogsModule/common/createTestPurchaseMethodSelectOption';
import { createKnmSubjectTypeSelectOption } from 'CorePackage/CatalogsModule/common/createKnmSubjectTypeSelectOption';
import { createKnmSupervisionTypeExtendedSelectOption } from 'CorePackage/CatalogsModule/common/createKnmSupervisionTypeSelectOption';
import { createKnmReasonExtendedSelectOption } from 'CorePackage/CatalogsModule/common/createKnmReasonTypeSelectOption';
import { monthSelectOptions } from 'KnmPackage/MainModule/constants/monthSelectOptions';
import { createKnoOrganisationSelectOption } from 'CorePackage/CatalogsModule/common/createKnoOrganisationSelectOption';
import { createKnmInspectorTypeSelectOption } from 'CorePackage/CatalogsModule/common/createKnmInspectorTypeSelectOption';
import { createKnmKnoOrganizationFunctionSelectOption } from 'KnmPackage/MainModule/common/createKnmKnoOrganizationFunctionSelectOption';
import { createKnmRejectReasonTypeSelectOption } from 'CorePackage/CatalogsModule/common/createKnmRejectReasonTypeSelectOption';
import { createRiskCategorySelectOption } from 'CorePackage/CatalogsModule/common/createRiskCategorySelectOption';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { KnmResultInfoTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmResultInfoTypeCodesEnum';
import { IKnmResultInfo } from 'APIPackage/KnmModule/interfaces/IKnmResultInfo';
import { createChecklistAnswerTypeSelectOption } from 'CorePackage/CatalogsModule/common/createChecklistAnswerTypeSelectOption';
import { createKnmAddressTypeSelectOption } from 'CorePackage/CatalogsModule/common/createKnmAddressTypeSelectOption';
import { createKnmObjectTypeSelectOption } from 'CorePackage/CatalogsModule/common/createKnmObjectTypeSelectOption';
import { IKnmErpFormResult } from 'KnmErpPackage/KnmErpResultsModule/interfaces/IKnmErpFormResult';
import { IKnmErpFormObject } from 'KnmErpPackage/KnmErpSubjectModule/interfaces/IKnmErpFormObject';
import { IKnmErpFormViolation } from 'KnmErpPackage/KnmErpViolationsModule/interfaces/IKnmErpFormViolation';
import { createKnmChecklistKnoOrganisationSelectOption } from 'KnmPackage/MainModule/common/createKnmChecklistKnoOrganisationSelectOption';
import { createKnmNotificationMethodSelectOption } from 'CorePackage/CatalogsModule/common/createKnmNotificationMethodSelectOption';
import { IKnmObject } from 'APIPackage/KnmModule/interfaces/IKnmObject';
import { createKnmDocumentTypeSelectOption } from 'CorePackage/CatalogsModule/common/createKnmDocumentTypeSelectOption';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';
import { createDistrictSelectOption } from 'CorePackage/CatalogsModule/common/createDistrictSelectOption';

import { knmErpSetInitialFormValues } from '../actions/knmErpActions';
import { IKnmErpForm } from '../interfaces/IKnmErpForm';
import { IKnmErpObjectSelectOption } from '../interfaces/IKnmErpObjectSelectOption';
import { IUserOrganisation } from '../../../APIPackage/AccountModule/interfaces/IUserOrganisation';
import { fetchProsecutorOrganisationsByErvkUuidAndVersion } from '../../../APIPackage/CatalogsModule/services/prosecutorOrganisationsApi';
import { createUserOrganizationWithDistrictSelectOption } from '../../../CorePackage/AccountModule/common/createUserOrganizationWithDistrictSelectOption';
import { createKnmProsecutorOrganisationSelectOption } from '../../../KnmPackage/MainModule/common/createKnmProsecutorOrganisationSelectOption';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { getKnmData, getKnmDataCreated, getKnmSystemType } from '../../../KnmPackage/MainModule/selectors/knmSelectors';
import { KnmSystemTypesEnum } from '../../../KnmPackage/MainModule/enums/KnmSystemTypesEnum';
import { getFullNameGuarantor } from '../../../KnmPmPackage/PmReasonsModule/common/getFullNameGuarantor';
import { convertKnmDataConditionTypeToFormValue } from '../../../KnmPackage/KnmDocumentsModule/common/convertKnmDataConditionTypeToFormValue';
import { getDictionarySelectOptionWithState } from '../../../CorePackage/DictionariesModule/common/getDictionarySelectOptionWithState';
import { DictionaryUUIDsEnum } from '../../../CorePackage/DictionariesModule/enums/DictionaryUUIDsEnum';
import { IDictionaryVersion } from '../../../APIPackage/DictionariesModule/interfaces/IDictionaryVersion';
import { IKnmErvk } from '../../../APIPackage/CatalogsModule/interfaces/IKnmErvk';
import { fetchDictionaryValue } from '../../../APIPackage/DictionariesModule/services/dictionariesApi';
import { IKnmErpErvkFormObject } from '../../KnmErpSubjectModule/interfaces/IKnmErpErvkFormObject';
import { DictionariesActionsEnum } from '../../../CorePackage/DictionariesModule/enums/DictionariesActionsEnum';
import { dictionariesItemValueGetSuccess } from '../../../CorePackage/DictionariesModule/actions/dictionariesActions';
import { getKnmErpInitialFormValues } from '../selectors/knmErpSelectors';
import { getInitialErpObjectErvk } from '../common/getInitialErpObjectErvk';
import { getDictionariesItemValue } from '../../../CorePackage/DictionariesModule/common/getDictionariesItemValue';
import { IKnmDictionary } from '../../../APIPackage/KnmModule/interfaces/IKnmDictionary';
import { getDictionariesItemValueData } from '../../../CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { createDictionarySupervisionTypeExtendedSelectOption } from '../../../CorePackage/DictionariesModule/common/createDictionarySupervisionTypeSelectOption';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { ISupervisionType } from '../../../APIPackage/DictionariesModule/interfaces/ISupervisionType';
import { IDictionaryValue } from '../../../APIPackage/DictionariesModule/interfaces/IDictionaryValue';
import { IKnmResult } from '../../../APIPackage/KnmModule/interfaces/IKnmResult';

interface IModifiedObject extends IKnmObject {
  listId: string;
  objectResult?: IKnmResult | null;
}

export function* watchKnmErpSaga() {
  yield takeEvery([KnmActionsEnum.KNM_GET_SUCCESS, KnmActionsEnum.KNM_UPDATE_SUCCESS], knmErpInit);
  yield takeEvery([KnmActionsEnum.KNM_GET_SUCCESS, KnmActionsEnum.KNM_UPDATE_SUCCESS], knmErpInitLinksSaga);
  yield takeEvery(DictionariesActionsEnum.DICTIONARIES_ITEM_VALUE_GET_SUCCESS, knmErpSetValueByOptionsSaga);
}

function modifyKnmDataObjects(objects: IKnm['objects'] | undefined) {
  const modifiedObjects: IModifiedObject[] | undefined = objects
    ?.map(object => ({
      ...object,
      listId: object.guid || uuidv4(), // ToDo: return 'object.guid'
      objectResult: object.result,
    }))
    ?.sort((a, b) => {
      if (a.main) {
        return -1;
      }
      if (b.main) {
        return 1;
      }

      return 0;
    });

  return modifiedObjects;
}

function* erpInitFormValuesSaga(action: ActionType<typeof knmGetSuccess>) {
  const { data: knmData } = action.payload;
  const state: IRootState = yield select();
  const orderDocument = knmData.approve?.documents?.find(document => document.type.code === 'ORDER');
  const headOrderDocument = knmData.approve?.documents?.find(document => document.type.code === 'ORDER_EXTENSION');
  const stopDate = knmData.stopDate || knmData.approve?.stopDate;
  const initialDurationDays = knmData.approve?.durationDays;
  const initialDurationHours = knmData.approve?.durationHours;
  const approveOrderDocument = knmData.approve?.documents?.find(document => document.type.code === 'REQUEST');
  const initialType = knmData?.type;
  const initialRejectReasons = knmData?.rejectReasons;

  let inspectionDecisionId: InspectionDecisionsEnum | undefined;

  if (knmData.approve && knmData.type) {
    if (knmData.rejectReasons?.length) {
      inspectionDecisionId = InspectionDecisionsEnum.DECLINED;
    } else if (knmData.approve.approved) {
      inspectionDecisionId = InspectionDecisionsEnum.APPROVED;
    }
  }

  const initialKind = knmData?.kind;
  const initialTestPurchaseMethod = knmData?.testPurchaseMethod;
  const initialSubjectType = knmData?.organization?.type;
  const initialSupervisionType = knmData?.supervisionType;
  const initialFederalLaw = knmData?.federalLaw;
  const initialMonth = knmData?.month;
  const initialReasons = knmData?.reasons;
  const initialKnoOrganization = knmData?.knoOrganization?.organization;

  const initialKnoOrganizationErvk = knmData?.knoOrganizationErvk;
  const initialFederalLawErvk = knmData?.federalLawErvk;
  const initialSupervisionTypeErvk = knmData?.supervisionTypeErvk;
  const initialKindErvk = knmData?.kindErvk;
  const initialSubjectTypeErvk = knmData?.subjectTypeErvk;
  const initialInspectorsErvk = knmData?.inspectorsErvk;
  const initialObjectsErvk = knmData?.objectsErvk;
  const initialReasonsErvk = knmData?.reasonsErvk;

  let ervkDictionary: IDictionaryVersion | undefined;
  let ervkValue: IKnmErvk | undefined;

  if (
    initialReasonsErvk &&
    initialReasonsErvk.length &&
    initialReasonsErvk[0].reason &&
    initialReasonsErvk[0].reason.ervkUuid
  ) {
    ervkDictionary = yield fetchDictionaryValue(DictionaryUUIDsEnum.ERVK, initialReasonsErvk[0].reason.ervkUuid);
  }

  if (ervkDictionary) {
    ervkValue = JSON.parse(ervkDictionary.value);
  }

  const modifiedInitialReasonsErvk = initialReasonsErvk?.map(item => {
    const initialReasonType = convertKnmDataConditionTypeToFormValue(state, KnmSystemTypesEnum.ERP, item?.reason);

    return {
      ...item,
      riskIndicators:
        item.riskIndicators?.map(indicatorValue => {
          const indicatorOption = indicatorValue.riskIndicator?.dictId
            ? getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.RISK_INDICATORY>(
                state,
                indicatorValue.riskIndicator?.dictId,
                indicatorValue.riskIndicator?.dictVersionId,
              )
            : undefined;

          return {
            guid: indicatorValue.guid,
            riskIndicator: indicatorOption,
            indicatorRiskAuto: indicatorValue.indicatorRiskAuto || undefined,
            indicatorRiskAttachment: indicatorValue.indicatorRiskAttachment || undefined,
            isNew: false,
          };
        }) || undefined,
      type:
        item?.reason && initialReasonType
          ? createKnmReasonExtendedSelectOption(
              item?.reason,
              { ...initialReasonType, dateBegin: ervkValue?.dateBegin || '', dateEnd: ervkValue?.dateEnd || '' },
              item?.reason?.flagProfvizit,
              item?.reason?.inspectionResults,
            )
          : undefined,
      text: item?.reason?.text || undefined,
      inspectionResults: item?.reason?.inspectionResults,
      ervkUuid: item.reason?.ervkUuid || undefined,
      title: item.reason?.title || undefined,
      shortTitle: item.reason?.shortTitle || undefined,
      date: convertInitialKnmDateToFormDate(item?.reason?.date, DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS),
      assignmentNumber: item?.reason?.assignmentNumber || undefined,
      assignmentDate: convertInitialKnmDateToFormDate(item?.reason?.assignmentDate, DATE_FORMAT_LONG_FNS),
      requirementDetails: item?.reason?.requirementDetails || undefined,
      programAttachment: item.reason?.programAttachment || undefined,
      programNumber: item.reason?.programNumber || undefined,
      fullName: getFullNameGuarantor(item.order?.guarantors[0]),
      dateRequirement:
        convertInitialKnmDateToFormDate(item?.reason?.dateRequirement, DATE_FORMAT_LONG_FNS) || undefined,
      links: item.reason?.links || undefined,
      listId: item?.numGuid || uuidv4(),
      order: item.order,
      reason: item.reason,
      lastConductedKnmDate: convertInitialKnmDateToFormDate(item?.reason?.lastConductedKnmDate, DATE_FORMAT_LONG_FNS),
    };
  });

  const initialCollaboratingOrganizations = knmData?.knoOrganization?.collaboratingOrganizations;
  const initialInspectors = knmData?.inspectors;
  const initialKnoOrganizationFunctions = knmData?.knoOrganization?.functions;
  const initialRiskCategory = knmData?.riskCategory;
  const knmDataObjects = knmData?.objects;
  const modifiedKnmDataObjects = modifyKnmDataObjects(knmDataObjects);
  const initialAuditedPersonAuthority = knmData?.auditedPersonAuthority;

  let initialFormObjectsResults: IKnmErpForm['objectsResults'];
  let initialFormResultsCancellationInfo: IKnmErpForm['resultsCancellationInfo'];
  let initialFormViolations: IKnmErpForm['violations'];
  let initialFormViolationsLawsuits: IKnmErpForm['violationsLawsuits'];
  let initialFormChecklists: IKnmErpForm['checklists'];

  (modifiedKnmDataObjects ?? initialObjectsErvk)?.forEach(knmDataObject => {
    const { objectResult } = knmDataObject;
    const objectChecklists = knmDataObject.checklists;
    const objectAddress = knmDataObject.notInFIAS ? knmDataObject.manualAddress : knmDataObject.address;
    const objectSelectOption: IKnmErpObjectSelectOption = {
      id: knmDataObject.guid,
      address: objectAddress || '',
      guid: knmDataObject.guid,
      label: objectAddress || '',
      value: objectAddress,
    };

    if (objectResult) {
      const initialObjectResult: IKnmErpFormResult = {
        listId: objectResult.guid || uuidv4(),
        object: objectSelectOption,
        guid: objectResult.guid,
        notInFIAS: !!objectResult.notInFIAS,
        isAddressHazard: !!objectResult.isAddressHazard,
        fiasRegion:
          !objectResult.notInFIAS && objectResult.district
            ? createDistrictSelectOption(objectResult.district)
            : undefined,
        region:
          objectResult.notInFIAS && objectResult.district
            ? createDistrictSelectOption(objectResult.district)
            : undefined,
        actCreateDate: convertInitialKnmDateToFormDate(
          objectResult.actCreateDate,
          DATE_FORMAT_LONG_MINUTE_FNS,
          DATE_FORMAT_ISO_FNS,
        ),
        actAddress: objectResult.actAddress,
        ...(objectResult.actAddressType && {
          actAddressType: {
            ...objectResult.actAddressType,
            label: objectResult.actAddressType.name,
            value: objectResult.actAddressType.code,
          },
        }),
        startDate: convertInitialKnmDateToFormDate(
          objectResult.startDate,
          DATE_FORMAT_LONG_MINUTE_FNS,
          DATE_FORMAT_ISO_FNS,
        ),
        durationDays: typeof objectResult.durationDays === 'number' ? `${objectResult.durationDays}` : undefined,
        durationHours: typeof objectResult.durationHours === 'number' ? `${objectResult.durationHours}` : undefined,
        impossibleConductionReason: objectResult.impossibleConductionReason,
        cancellationInfo: objectResult.cancellationInfo,
        actWasNotReadInfo: objectResult.actWasNotReadInfo,
        wrongNotificationData: objectResult.wrongNotificationData,
        violationLackInfo: objectResult.violationLackInfo,
        inspectors: objectResult.inspectors?.map(inspector => ({
          ...inspector,
          type: {
            ...inspector.type,
            label: inspector.type.name,
            value: inspector.type.code,
          },
        })),
        delegates: objectResult.delegates?.map(delegate => ({
          ...delegate,
          type: {
            ...delegate.type,
            label: delegate.type.name,
            value: delegate.type.code,
          },
        })),
        additionalInfo: objectResult?.additionalInfo?.reduce(
          (filteredAdditionalInfo: IKnmErpFormResult['additionalInfo'] | undefined, additionalInfoItem) => {
            let newFilteredAdditionalInfo = filteredAdditionalInfo ? [...filteredAdditionalInfo] : undefined;

            if (additionalInfoItem.type.code === KnmResultInfoTypeCodesEnum.UNDOIG_SEC_I) {
              return newFilteredAdditionalInfo?.map(additionalInfo => {
                if (additionalInfo?.guid) {
                  return additionalInfo;
                }

                return {
                  ...additionalInfo,
                  guid: uuidv4(),
                };
              });
            }
            if (!newFilteredAdditionalInfo) {
              newFilteredAdditionalInfo = [];
            }
            newFilteredAdditionalInfo.push({
              ...additionalInfoItem,
              type: {
                ...additionalInfoItem.type,
                label: additionalInfoItem.type.name,
                value: additionalInfoItem.type.code,
              },
              actWasNotRead:
                additionalInfoItem?.type?.code === KnmResultInfoTypeCodesEnum.ACT_WAS_NOT_READ_NOTE &&
                typeof objectResult.actWasNotRead === 'boolean'
                  ? objectResult.actWasNotRead
                  : undefined,
              guid: !additionalInfoItem?.guid ? uuidv4() : additionalInfoItem.guid,
            });

            return newFilteredAdditionalInfo;
          },
          undefined,
        ),
        empty: objectResult.empty,
      };

      if (!initialFormObjectsResults) {
        initialFormObjectsResults = [];
      }
      initialFormObjectsResults.push(initialObjectResult);

      const objectResultCancellationInfo: IKnmResultInfo | undefined = objectResult?.additionalInfo?.find(
        objectResultAdditionalInfo => objectResultAdditionalInfo.type.code === KnmResultInfoTypeCodesEnum.UNDOIG_SEC_I,
      );

      if (objectResultCancellationInfo && initialObjectResult.object) {
        if (!initialFormResultsCancellationInfo) {
          initialFormResultsCancellationInfo = [];
        }
        initialFormResultsCancellationInfo.push({
          result: initialObjectResult.object,
          type: objectResultCancellationInfo.type,
          information: objectResultCancellationInfo.information,
          guid: objectResultCancellationInfo.guid,
          listId: objectResultCancellationInfo.guid || uuidv4(),
        });
      }

      objectResult.violations?.forEach(objectResultViolation => {
        if (!initialFormViolations) {
          initialFormViolations = [];
        }
        const initialFormViolation: IKnmErpFormViolation = {
          ...objectResultViolation,
          note: objectResultViolation.note || undefined,
          act: objectResultViolation.act || undefined,
          result: initialObjectResult.object,
          type: objectResultViolation.type
            ? {
                ...objectResultViolation.type,
                label: objectResultViolation.type.name,
                value: objectResultViolation.type.code,
              }
            : undefined,
          legalBases: objectResultViolation.legalBases?.map(legalBasis => ({
            ...legalBasis,
            id: uuidv4(),
          })),
          injunctions: objectResultViolation.injunctions?.map(injunction => ({
            ...injunction,
            appointmentDate: convertInitialKnmDateToFormDate(injunction.appointmentDate, DATE_FORMAT_LONG_FNS),
            executionDeadline: convertInitialKnmDateToFormDate(injunction.executionDeadline, DATE_FORMAT_LONG_FNS),
          })),
          listId: objectResultViolation.guid || uuidv4(),
        };

        initialFormViolations.push(initialFormViolation);

        if (objectResultViolation.lawsuits) {
          if (!initialFormViolationsLawsuits) {
            initialFormViolationsLawsuits = [];
          }
          initialFormViolationsLawsuits.push({
            violation: initialFormViolation.result
              ? {
                  id: initialFormViolation.listId,
                  label: initialFormViolation.result.label,
                  value: initialFormViolation.result.value,
                }
              : undefined,
            lawsuits: objectResultViolation.lawsuits?.map(violationLawsuit => ({
              ...violationLawsuit,
              lawsuitType: {
                ...violationLawsuit.lawsuitType,
                label: violationLawsuit.lawsuitType.name,
                value: violationLawsuit.lawsuitType.code,
              },
            })) || [undefined],
            listId: initialObjectResult.object?.id || uuidv4(),
          });
        }
      });
    }

    objectChecklists?.forEach(objectChecklist => {
      if (!initialFormChecklists) {
        initialFormChecklists = [];
      }
      initialFormChecklists.push({
        ...objectChecklist,
        name: objectChecklist.name || undefined,
        approval: objectChecklist.approval || undefined,
        questions: objectChecklist.questions?.map(initialQuestion => ({
          ...initialQuestion,
          question: initialQuestion.question || undefined,
          legalBase: initialQuestion.legalBase || undefined,
          answerType: initialQuestion.answerType
            ? createChecklistAnswerTypeSelectOption(initialQuestion.answerType)
            : undefined,
          answer: initialQuestion.answer || undefined,
          guid: initialQuestion.guid || undefined,
          dateCreate: initialQuestion.dateCreate || undefined,
          index: initialQuestion.index || undefined,
          listId: initialQuestion.guid || uuidv4(),
        })),
        template: objectChecklist.template || undefined,
        guid: objectChecklist.guid || undefined,
        fullnessType: objectChecklist.fullnessType || undefined,
        knoOrganization: objectChecklist.knoOrganization
          ? createKnmChecklistKnoOrganisationSelectOption(objectChecklist.knoOrganization)
          : undefined,
        dateCreate: objectChecklist.dateCreate || undefined,
        object: objectSelectOption,
        listId: objectChecklist.guid || uuidv4(),
        ervkCheckList: objectChecklist.ervkCheckList
          ? {
              ...objectChecklist.ervkCheckList,
              id: objectChecklist.ervkCheckList.id,
              label: `${objectChecklist.ervkCheckList.number} - ${objectChecklist.ervkCheckList.description}`,
              value: {
                dictId: DictionaryUUIDsEnum.ERVK_CHECK_LIST,
                dictVersionId: objectChecklist.ervkCheckList.id || '',
              },
            }
          : undefined,
      });
    });
  });
  const initialNoticeMethod = knmData?.noticeMethod;
  const initialKnmDataRequirements = knmData?.requirements;
  const initialDocuments = knmData?.documents;

  yield put(
    knmErpSetInitialFormValues({
      testPurchaseDescription: knmData.testPurchaseDescription || undefined,
      orderNumber: orderDocument?.atr || undefined,
      orderDate: convertInitialKnmDateToFormDate(orderDocument?.date, DATE_FORMAT_LONG_FNS),
      headOrderNumber: headOrderDocument?.atr || undefined,
      headOrderDate: convertInitialKnmDateToFormDate(headOrderDocument?.date, DATE_FORMAT_LONG_FNS),
      startDate: convertInitialKnmDateToFormDate(knmData.startDate || knmData.approve?.startDate, DATE_FORMAT_LONG_FNS),
      stopDate: convertInitialKnmDateToFormDate(stopDate, DATE_FORMAT_LONG_FNS),
      durationDays: typeof initialDurationDays === 'number' ? `${initialDurationDays}` : undefined,
      durationHours: typeof initialDurationHours === 'number' ? `${initialDurationHours}` : undefined,
      inspectionTarget: knmData.approve?.inspectionTarget || undefined,
      extensionOfKnmTime: Boolean(headOrderDocument?.atr || headOrderDocument?.date),
      legalBases: knmData.legalBases?.map(legalBasis => ({
        ...legalBasis,
        id: uuidv4(),
      })),
      events: knmData.events?.map(event => ({
        ...event,
        startDate: convertInitialKnmDateToFormDate(event.startDate, DATE_FORMAT_LONG_FNS),
        stopDate: convertInitialKnmDateToFormDate(event.stopDate, DATE_FORMAT_LONG_FNS),
        listId: event.guid,
      })),
      collaboratingKnoName: knmData.knoOrganization?.collaboratingKnoName || undefined,
      inspectionDecision: inspectionDecisionId
        ? inspectionDecisionOptions.find(option => option.id === inspectionDecisionId)
        : undefined,
      approveOrderNumber: approveOrderDocument?.atr || undefined,
      approveOrderDate: convertInitialKnmDateToFormDate(approveOrderDocument?.date, DATE_FORMAT_LONG_FNS),
      decisionDate: convertInitialKnmDateToFormDate(knmData.approve?.decisionDate, DATE_FORMAT_LONG_FNS),
      decisionPlace: knmData.approve?.decisionPlace || undefined,
      signerFullName: knmData.approve?.signerFullName || undefined,
      signerPosition: knmData.approve?.signerPosition || undefined,
      auditedPersonAuthority: initialAuditedPersonAuthority?.map(authority => ({
        listId: uuidv4(),
        authority,
      })),
      licenseReason: knmData.organization?.licenseReason || undefined,
      licenseReasonStartDate: convertInitialKnmDateToFormDate(
        knmData.organization?.licenseReasonStartDate,
        DATE_FORMAT_LONG_FNS,
      ),
      licenseReasonKnmEndDate: convertInitialKnmDateToFormDate(
        knmData.organization?.licenseReasonKnmEndDate,
        DATE_FORMAT_LONG_FNS,
      ),
      ogrn: knmData.organization?.ogrn || undefined,
      inn: knmData.organization?.inn,
      mspCode: knmData.organization?.mspCode || undefined,
      organizationName: knmData.organization?.organizationName?.replace(/(\\r\\n|\\n|\\r)/gm, ' ') || undefined,
      organizationAutocomplete:
        typeof knmData.organization?.autocomplete === 'boolean' ? knmData.organization?.autocomplete : undefined,
      registrationDate: convertInitialKnmDateToFormDate(knmData.organization?.registrationDate, DATE_FORMAT_LONG_FNS),
      organizationStatus: knmData.organization?.status || undefined,
      isSmallEnterpriseText:
        knmData.organization?.isSmallEnterprise ||
        knmData.organization?.isSmallEnterpriseFns ||
        typeof knmData.organization?.checkSubjectResult?.msp?.id === 'number' ||
        knmData.organization?.checkSubjectResult?.msp?.legacyId ||
        knmData.organization?.mspCode !== null
          ? (knmData.organization?.mspCode !== MspCodesEnum.NON_EXIST && 'Да') || 'Нет'
          : undefined,
      noticeDate: convertInitialKnmDateToFormDate(knmData.noticeDate, DATE_FORMAT_LONG_FNS),
      impossibleConductionReason: knmData.approve?.impossibleConductionReason || undefined,
      type: initialType ? createKnmTypeSelectOption(initialType) : undefined,
      typeErvk: initialType ? createKnmTypeSelectOption(initialType) : undefined,
      kind: initialKind ? createKnmExecutionTypeSelectOption(initialKind) : undefined,
      kindErvk: initialKindErvk ?? undefined,
      testPurchaseMethod: initialTestPurchaseMethod
        ? createTestPurchaseMethodSelectOption(initialTestPurchaseMethod)
        : undefined,
      subjectType: (initialSubjectType && createKnmSubjectTypeSelectOption(initialSubjectType)) ?? undefined,
      subjectTypeErvk: initialSubjectTypeErvk ?? undefined,
      supervisionType: initialSupervisionType
        ? createKnmSupervisionTypeExtendedSelectOption({
            ...initialSupervisionType,
            federalLaw: initialFederalLaw || undefined, // ToDo: initialFederalLaw: null - backend error ?
          })
        : undefined,
      month:
        typeof initialMonth === 'number' ? monthSelectOptions.find(option => option.value === initialMonth) : undefined,
      reasons: initialReasons?.map(reason => ({
        ...reason,
        listId: reason.id || uuidv4(),
        date: convertInitialKnmDateToFormDate(reason.date, DATE_FORMAT_LONG_FNS),
        type: reason.type ? createKnmReasonExtendedSelectOption(reason, reason.type) : undefined,
        approveRequired: reason.approveRequired || false,
        assignmentDate: convertInitialKnmDateToFormDate(reason.assignmentDate, DATE_FORMAT_LONG_FNS),
        links: reason.links || undefined,
        lastConductedKnmDate: reason.lastConductedKnmDate === null ? undefined : reason.lastConductedKnmDate,
        dateRequirement: convertInitialKnmDateToFormDate(reason.dateRequirement, DATE_FORMAT_LONG_FNS) || undefined,
        requirementDetails: reason.requirementDetails || undefined,
        assignmentNumber: reason.assignmentNumber || undefined,
        title: reason.title || undefined,
        shortTitle: reason.shortTitle || undefined,
      })),
      knoOrganization: initialKnoOrganization ? createKnoOrganisationSelectOption(initialKnoOrganization) : undefined,
      knoOrganizationErvk: initialKnoOrganizationErvk ?? undefined,
      federalLaw: initialFederalLawErvk ?? undefined,
      supervisionTypeErvk: initialSupervisionTypeErvk ?? undefined,
      collaboratingOrganizations: initialCollaboratingOrganizations?.map(collaboratingOrganization => ({
        ...collaboratingOrganization,
        organization:
          collaboratingOrganization.organization &&
          createKnoOrganisationSelectOption(collaboratingOrganization.organization),
        listId: collaboratingOrganization.organization.id,
      })),
      inspectors: initialInspectors?.map(inspector => ({
        ...inspector,
        fullName: inspector.fullName,
        type: inspector.type && createKnmInspectorTypeSelectOption(inspector.type),
        listId: inspector.guid || uuidv4(),
      })),
      inspectorsErvk: initialInspectorsErvk,
      knoOrganizationFunctions: initialKnoOrganizationFunctions?.map(
        knoOrganizationFunction =>
          knoOrganizationFunction && createKnmKnoOrganizationFunctionSelectOption(knoOrganizationFunction),
      ),
      rejectReasons: initialRejectReasons?.map(rejectReason => ({
        ...rejectReason,
        type: rejectReason.type && createKnmRejectReasonTypeSelectOption(rejectReason.type),
        listId: rejectReason.id,
      })),
      riskCategory: initialRiskCategory ? createRiskCategorySelectOption(initialRiskCategory) : undefined,
      objects: modifiedKnmDataObjects?.map(object => ({
        listId: object.listId,
        main: object.main,
        address: object.address,
        notInFIAS: object.notInFIAS ?? undefined,
        fiasRegion: (object.fiasRegion && createDistrictSelectOption(object.fiasRegion)) ?? undefined,
        manualAddress: object.manualAddress ?? undefined,
        addressType: object.addressType && createKnmAddressTypeSelectOption(object.addressType),
        objectType: object.objectType && createKnmObjectTypeSelectOption(object.objectType),
        isAddressHazard: object.addressHazard ?? object.isAddressHazard ?? undefined,
        riskCategory:
          object.riskCategory && object.riskCategory.id && object.riskCategory.name && object.riskCategory.code
            ? createRiskCategorySelectOption(object.riskCategory)
            : undefined, // ToDo: BE - id: null, name: null, code: null ?
        supervisions: object.supervisions?.reduce(
          (formSupervisions: IKnmErpFormObject['supervisions'], supervision) => {
            let newFormSupervisions = formSupervisions ? [...formSupervisions] : undefined;

            if (supervision.supervisionType) {
              if (!newFormSupervisions) {
                newFormSupervisions = [];
              }
              newFormSupervisions.push(createKnmSupervisionTypeExtendedSelectOption(supervision.supervisionType));
            }

            return newFormSupervisions;
          },
          undefined,
        ),
        guid: object.guid,
        streetGuid: object.streetGuid,
        houseGuid: object.houseGuid,
        roomGuid: object.roomGuid,
        steadGuid: object.steadGuid,
        addressFns: object.addressFns,
        autocomplete: typeof object.autocomplete === 'boolean' ? object.autocomplete : undefined,
        correlationToken: object.correlationToken,
      })),
      objectsErvk: initialObjectsErvk?.map(object => {
        return getInitialErpObjectErvk(state, object);
      }),
      objectsResults: initialFormObjectsResults,
      resultsCancellationInfo: initialFormResultsCancellationInfo,
      reasonsErvk: modifiedInitialReasonsErvk,
      violations: initialFormViolations,
      violationsLawsuits: initialFormViolationsLawsuits,
      checklists: initialFormChecklists,
      noticeMethod: initialNoticeMethod ? createKnmNotificationMethodSelectOption(initialNoticeMethod) : undefined,
      requirements: initialKnmDataRequirements?.map(knmDataRequirement => ({
        ...knmDataRequirement,
        documents: knmDataRequirement.documents || undefined,
        listId: knmDataRequirement.guid || uuidv4(),
      })),
      documents: initialDocuments?.map(document => ({
        ...document,
        type: document?.type ? createKnmDocumentTypeSelectOption(document?.type) : undefined,
        attachments: document.attachments || undefined,
        listId: document.guid || uuidv4(),
      })),
      created: convertInitialKnmDateToFormDate(knmData.created, DATE_FORMAT_LONG_FNS),
    }),
  );
}

function* knmErpInit(action: ActionType<typeof knmGetSuccess>) {
  const { knmErknm, pmErknm } = action.payload.data;

  if (action.payload.data.version === KnmVersionsEnum.ERP || (!knmErknm && !pmErknm)) {
    yield erpInitFormValuesSaga(action);
    yield knmErpInitSelectOptionsSaga(action);
    yield knmPmInitProsecutorOrganizationSaga(action);
  }
  // if (!pmErknm && (!knmErknm || action.payload.data.version === KnmVersionsEnum.ERP)) {
  //   yield knmPmInitFormValuesSaga(action);
  //   yield knmPmInitProsecutorOrganizationSaga(action);
  // }
}

function* knmErpInitSelectOptionsSaga(action: ActionType<typeof knmGetSuccess>) {
  const { data: knmData } = action.payload;

  const initialObjects = knmData?.objectsErvk;
  const resultArrayActions: unknown[] = [];
  const currentDictionariesFromData: IKnmDictionary[] = [];
  const reasons = knmData?.reasonsErvk;

  if (reasons) {
    reasons?.forEach(item => {
      item.riskIndicators?.forEach(
        indicatorValue =>
          indicatorValue.riskIndicator && currentDictionariesFromData.push(indicatorValue.riskIndicator),
      );
    });
  }
  if (initialObjects) {
    initialObjects?.forEach(object => {
      if (object.objectType && object.objectType.dictVersionId) {
        currentDictionariesFromData.push(object.objectType);
      }
      if (object.riskCategory && object.riskCategory.dictVersionId) {
        currentDictionariesFromData.push(object.riskCategory);
      }
      if (object.supervisions) {
        object.supervisions?.forEach(supervision => {
          if (supervision.supervisionType?.dictVersionId) {
            currentDictionariesFromData.push(supervision.supervisionType);
          }
        });
      }
    });
  }

  uniq(currentDictionariesFromData).forEach(item => resultArrayActions.push(getDictionariesItemValue(item)));
  yield all(resultArrayActions);
}

function* knmErpInitLinksSaga(action: ActionType<typeof knmGetSuccess>) {
  function* funcTry() {
    const {
      data: { id, version, links: knmDataLinks, knmErknm, pmErknm },
    } = action.payload;

    if (typeof id === 'number' && (version === KnmVersionsEnum.ERP || (!knmErknm && !pmErknm))) {
      const links: IKnmLink[] = yield fetchKnmLinks(id);

      yield put(
        knmErpSetInitialFormValues({
          links: links.map(item => ({
            ...item,
            guid: knmDataLinks?.find(dataItem => dataItem.erpId === item.erpId)?.guid || null,
          })),
        }),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* knmPmInitProsecutorOrganizationSaga(action: ActionType<typeof knmGetSuccess>) {
  function* funcTry() {
    const state: IRootState = yield select();
    const knmSystemType = getKnmSystemType(state);
    const isErpKnmSystemType = knmSystemType === KnmSystemTypesEnum.ERP;

    if (isErpKnmSystemType) {
      const { data: currentKnmData } = action.payload;
      const { prosecutorOrganization } = currentKnmData;

      if (prosecutorOrganization?.ervkUuid && prosecutorOrganization?.ervkVersionUuid) {
        const ervkProsecutorOrganization: IUserOrganisation = yield fetchProsecutorOrganisationsByErvkUuidAndVersion(
          prosecutorOrganization?.ervkUuid,
          prosecutorOrganization?.ervkVersionUuid,
        );

        const userOrganizationWithDistrictSelectOption = createUserOrganizationWithDistrictSelectOption(
          ervkProsecutorOrganization,
        );

        yield put(
          knmErpSetInitialFormValues({
            prosecutorOrganization: userOrganizationWithDistrictSelectOption,
          }),
        );
      } else {
        const initialProsecutorOrganization = currentKnmData?.prosecutorOrganization;
        const initialDistrictName = currentKnmData?.district?.name || null;
        const initialDistrictId = currentKnmData?.district?.id || null;

        yield put(
          knmErpSetInitialFormValues({
            prosecutorOrganization:
              initialProsecutorOrganization?.id &&
              !initialProsecutorOrganization?.ervkUuid &&
              initialProsecutorOrganization?.name
                ? createKnmProsecutorOrganisationSelectOption({
                    prosecutorOrganization: {
                      ...initialProsecutorOrganization,
                      id: initialProsecutorOrganization.id,
                      name: initialProsecutorOrganization?.name,
                    },
                    districtName: initialDistrictName,
                    districtId: initialDistrictId?.toString() || null,
                  })
                : undefined,
          }),
        );
      }
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* knmErpSetValueByOptionsSaga(action: ActionType<typeof dictionariesItemValueGetSuccess>) {
  const { dictionaryUUID } = action.payload;
  const state: IRootState = yield select();
  const currentKnmData = getKnmData(state);
  const initialFormValues = getKnmErpInitialFormValues(state);
  const currentObjects = currentKnmData?.objectsErvk;
  const settings = getAdminFetchSettings(state);
  const initialReasons = currentKnmData?.reasonsErvk;

  switch (dictionaryUUID) {
    case DictionaryUUIDsEnum.SUBJECT_TYPE: {
      yield put(
        knmErpSetInitialFormValues({
          objectsErvk: currentObjects?.map((object, index) => {
            const selectOption = object.objectType?.dictId
              ? getDictionarySelectOptionWithState<typeof dictionaryUUID>(
                  state,
                  object.objectType?.dictId,
                  object.objectType?.dictVersionId,
                )
              : undefined;

            const initialObject: IKnmErpErvkFormObject | undefined = initialFormValues?.objectsErvk?.[index];

            if (initialObject) {
              return {
                ...initialObject,
                objectType: selectOption,
              };
            }

            return getInitialErpObjectErvk(state, object);
          }),
        }),
      );
      break;
    }
    case DictionaryUUIDsEnum.RISK_CATEGORY: {
      yield put(
        knmErpSetInitialFormValues({
          objectsErvk: currentObjects?.map((object, index) => {
            const initialObject: IKnmErpErvkFormObject | undefined = initialFormValues?.objectsErvk?.[index];
            const riskCategorySelectOption = object.riskCategory?.dictId
              ? getDictionarySelectOptionWithState<typeof dictionaryUUID>(
                  state,
                  object.riskCategory?.dictId,
                  object.riskCategory?.dictVersionId,
                )
              : undefined;

            if (initialObject) {
              return {
                ...initialObject,
                riskCategory: riskCategorySelectOption,
              };
            }

            return getInitialErpObjectErvk(state, object);
          }),
        }),
      );
      break;
    }
    case DictionaryUUIDsEnum.SUPERVISION_TYPE: {
      const isUseTransferringAuthorities = isLaterThanAdminSetting(
        getKnmDataCreated(state),
        settings,
        AdminSettingCodesEnum.TRANSFERRING_AUTHORITIES_4_1_22_STARTS_FROM,
      );

      yield put(
        knmErpSetInitialFormValues({
          objectsErvk: currentObjects?.map((object, index) => {
            const initialObject: IKnmErpErvkFormObject | undefined = initialFormValues?.objectsErvk?.[index];
            const supervisions =
              object.supervisions
                ?.filter(supervision => supervision.supervisionType?.dictId)
                ?.map(supervision => {
                  // @ts-ignore
                  const dictionaryValue: IDictionaryValue<ISupervisionType> = getDictionariesItemValueData(
                    state,
                    supervision.supervisionType?.dictId,
                    supervision.supervisionType?.dictVersionId,
                  );

                  return {
                    listId: supervision.numGuid || uuidv4(),
                    supervision: createDictionarySupervisionTypeExtendedSelectOption(
                      supervision.supervisionType?.dictId,
                      dictionaryValue,
                      isUseTransferringAuthorities,
                    ),
                  };
                }) || undefined;

            if (initialObject) {
              return {
                ...initialObject,
                supervisions,
              };
            }

            return getInitialErpObjectErvk(state, object);
          }),
        }),
      );
      break;
    }
    case DictionaryUUIDsEnum.RISK_INDICATORY: {
      const isUseRiskIndicatorShortTitle = isLaterThanAdminSetting(
        initialFormValues?.created,
        settings,
        AdminSettingCodesEnum.USE_RISK_INDICATOR_SHORT_TITLE_STARTS_FROM,
      );

      yield put(
        knmErpSetInitialFormValues({
          reasonsErvk: initialReasons?.map((item, index) => {
            return {
              ...item,
              ...initialFormValues?.reasonsErvk?.[index],
              riskIndicators:
                item.riskIndicators?.map(indicatorValue => {
                  const indicatorOption = indicatorValue.riskIndicator?.dictId
                    ? getDictionarySelectOptionWithState<typeof dictionaryUUID>(
                        state,
                        indicatorValue.riskIndicator?.dictId,
                        indicatorValue.riskIndicator?.dictVersionId,
                      )
                    : undefined;

                  return {
                    guid: indicatorValue.guid,
                    riskIndicator:
                      isUseRiskIndicatorShortTitle && indicatorOption
                        ? { ...indicatorOption, label: indicatorOption.shortTitle || indicatorOption.title }
                        : indicatorOption,
                    indicatorRiskAuto: indicatorValue.indicatorRiskAuto || undefined,
                    indicatorRiskAttachment: indicatorValue.indicatorRiskAttachment || undefined,
                    isNew: false,
                  };
                }) || undefined,
              links: item.reason?.links || undefined,
              passportLinks: item.reason?.passportLinks || undefined,
              violationEvidences: item?.reason?.videoFixations
                ? item?.reason?.videoFixations?.map(fix => ({
                    guid: fix.guid || null,
                    date: convertInitialKnmDateToFormDate(fix.dateVideo, DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS),
                    description: fix.descVideo,
                    link: fix.linkVideo,
                    originPassportId: fix.originPassportId,
                    listId: uuidv4(),
                    isNew: false,
                  }))
                : undefined,
              listId: item?.numGuid || uuidv4(),
            };
          }),
        }),
      );
      break;
    }
    default:
  }
}
